// frontend/src/components/admin/FileIntegrationTab.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  analyseFile, analyseFileUrl, previewIngest, executeIngest,
  saveSchedule, listSchedules, getHistory, rollbackIngest,
} from '../../api/fileIntegrationService';

// ─── Constants ───────────────────────────────────────────────────────────────
const STEPS = { CONFIGURE: 'configure', MAPPING: 'mapping', PREVIEW: 'preview', DONE: 'done' };

const COLLECTION_LABELS = {
  customers: 'Customers', appointments: 'Appointments', transactions: 'Transactions',
  services: 'Services', products: 'Products', staff: 'Staff', promotions: 'Promotions',
};

const DOW_LABELS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// ─── Shared Styles ────────────────────────────────────────────────────────────
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };
const selectStyle = { fontSize: 13, padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' };
const inputStyle = { fontSize: 14, padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', width: '100%', boxSizing: 'border-box' };
const primaryBtnStyle = { background: 'var(--primary, #E64C31)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
const secondaryBtnStyle = { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
const thStyle = { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 };
const tdStyle = { padding: '9px 12px', verticalAlign: 'middle' };

// ─── Helpers ─────────────────────────────────────────────────────────────────
function ConfidenceBadge({ score }) {
  const colour = score >= 80 ? '#16a34a' : score >= 40 ? '#d97706' : '#6b7280';
  const label  = score >= 80 ? 'High'   : score >= 40 ? 'Medium'  : 'Low';
  return <span style={{ fontSize: 11, fontWeight: 600, color: colour, background: `${colour}18`, borderRadius: 4, padding: '1px 6px', marginLeft: 6 }}>{label}</span>;
}

function StepBar({ step }) {
  const steps = [
    { key: STEPS.CONFIGURE, label: '1. Configure' },
    { key: STEPS.MAPPING,   label: '2. Map Fields' },
    { key: STEPS.PREVIEW,   label: '3. Preview' },
    { key: STEPS.DONE,      label: '4. Done' },
  ];
  const currentIdx = steps.findIndex(s => s.key === step);
  return (
    <div style={{ display: 'flex', gap: 0, marginBottom: 28 }}>
      {steps.map((s, i) => {
        const done = i < currentIdx; const active = i === currentIdx;
        return (
          <React.Fragment key={s.key}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, background: active ? 'var(--primary, #E64C31)' : done ? '#dcfce7' : '#f3f4f6', color: active ? '#fff' : done ? '#16a34a' : '#6b7280', fontWeight: active ? 700 : 500, fontSize: 13, transition: 'all .2s' }}>
              {done && <span>✓</span>}{s.label}
            </div>
            {i < steps.length - 1 && <div style={{ alignSelf: 'center', padding: '0 4px', color: '#d1d5db', fontSize: 16 }}>›</div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TabBtn({ id, active, onClick, children }) {
  return (
    <button onClick={() => onClick(id)} style={{ padding: '9px 22px', fontWeight: active ? 700 : 500, fontSize: 13, border: 'none', borderBottom: active ? '2px solid var(--primary, #E64C31)' : '2px solid transparent', background: 'transparent', cursor: 'pointer', color: active ? 'var(--primary, #E64C31)' : '#6b7280' }}>
      {children}
    </button>
  );
}

// ─── Step 1: Configure ───────────────────────────────────────────────────────
function ConfigureStep({ onAnalysed, salonId }) {
  const [name, setName]           = useState('');
  const [sourceTab, setSourceTab] = useState('upload');
  const [file, setFile]           = useState(null);
  const [fileUrl, setFileUrl]     = useState('');
  const [dragging, setDragging]   = useState(false);
  const [scheduleType, setScheduleType] = useState('one-time'); // 'one-time' | 'recurring'
  const [frequency, setFrequency] = useState('daily');
  const [time, setTime]           = useState('09:00');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [endType, setEndType]     = useState('forever');
  const [maxCycles, setMaxCycles] = useState(10);
  const [endDate, setEndDate]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const inputRef = useRef();

  const FILE_MAX_BYTES = 5 * 1024 * 1024;

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'json'].includes(ext)) { setError('Unsupported format. Use a .csv or .json file.'); return; }
    if (f.size > FILE_MAX_BYTES) { setError('File too large. 5 MB maximum allowed.'); return; }
    setError(''); setFile(f);
  };

  const canAnalyse = name.trim() && (sourceTab === 'upload' ? !!file : !!fileUrl.trim());

  const handleAnalyse = async () => {
    if (!canAnalyse) return;
    setLoading(true); setError('');
    try {
      let result;
      const scheduleConfig = { name: name.trim(), type: scheduleType, fileUrl: sourceTab === 'url' ? fileUrl.trim() : null, frequency, time, dayOfWeek: Number(dayOfWeek), dayOfMonth: Number(dayOfMonth), endType, maxCycles: endType === 'cycles' ? Number(maxCycles) : null, endDate: endType === 'date' ? endDate : null };
      if (sourceTab === 'url') {
        result = await analyseFileUrl(salonId, fileUrl.trim());
        onAnalysed({ fileUrl: fileUrl.trim(), scheduleConfig, ...result });
      } else {
        result = await analyseFile(salonId, file);
        onAnalysed({ file, scheduleConfig, ...result });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 18, fontSize: 18 }}>Configure Upload Request</h3>

      {/* Request name */}
      <div style={{ marginBottom: 22 }}>
        <label style={labelStyle}>Request Name <span style={{ color: '#E64C31' }}>*</span></label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Monthly Transactions Sync" style={inputStyle} />
      </div>

      {/* File source */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>File Source</div>
        <div style={{ borderBottom: '1px solid #e5e7eb', display: 'flex', marginBottom: 18 }}>
          <TabBtn id="upload" active={sourceTab === 'upload'} onClick={setSourceTab}>📁 Upload from Computer</TabBtn>
          <TabBtn id="url" active={sourceTab === 'url'} onClick={setSourceTab}>🔗 File URL</TabBtn>
        </div>

        {sourceTab === 'upload' && (
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            style={{ border: `2px dashed ${dragging ? 'var(--primary,#E64C31)' : '#d1d5db'}`, borderRadius: 12, padding: '32px 24px', textAlign: 'center', cursor: 'pointer', background: dragging ? '#fff5f4' : '#fafafa', transition: 'all .2s' }}
          >
            <input ref={inputRef} type="file" accept=".csv,.json" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
            <div style={{ fontSize: 32, marginBottom: 6 }}>📂</div>
            {file ? (
              <div><div style={{ fontWeight: 600 }}>{file.name}</div><div style={{ color: '#6b7280', fontSize: 13 }}>{(file.size/1024).toFixed(1)} KB</div></div>
            ) : (
              <div style={{ color: '#6b7280', fontSize: 14 }}>Drag & drop or <span style={{ color: 'var(--primary,#E64C31)', fontWeight: 600 }}>click to browse</span></div>
            )}
          </div>
        )}

        {sourceTab === 'url' && (
          <div>
            <label style={labelStyle}>File URL (CSV or JSON)</label>
            <input type="url" value={fileUrl} onChange={e => setFileUrl(e.target.value)} placeholder="https://example.com/data/transactions.csv" style={inputStyle} />
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 5 }}>Must be publicly accessible. Also used for recurring auto-pulls.</p>
          </div>
        )}
      </div>

      {/* Schedule type */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Upload Frequency</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          {[['one-time','⬆ One-time Upload'],['recurring','🔁 Recurring Auto-Pull']].map(([val, lbl]) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 16px', borderRadius: 10, border: `2px solid ${scheduleType === val ? 'var(--primary,#E64C31)' : '#e5e7eb'}`, background: scheduleType === val ? '#fff5f4' : '#fafafa', fontWeight: scheduleType === val ? 700 : 500, fontSize: 13 }}>
              <input type="radio" value={val} checked={scheduleType === val} onChange={() => setScheduleType(val)} style={{ accentColor: 'var(--primary,#E64C31)' }} />
              {lbl}
            </label>
          ))}
        </div>

        {scheduleType === 'recurring' && (
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12, padding: '18px 20px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Frequency</label>
                <select value={frequency} onChange={e => setFrequency(e.target.value)} style={selectStyle}>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {frequency !== 'hourly' && (
                <div>
                  <label style={labelStyle}>Time (UTC)</label>
                  <input type="time" value={time} onChange={e => setTime(e.target.value)} style={{ ...selectStyle, padding: '5px 10px' }} />
                </div>
              )}
              {frequency === 'weekly' && (
                <div>
                  <label style={labelStyle}>Day of Week</label>
                  <select value={dayOfWeek} onChange={e => setDayOfWeek(e.target.value)} style={selectStyle}>
                    {DOW_LABELS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
              )}
              {frequency === 'monthly' && (
                <div>
                  <label style={labelStyle}>Day of Month</label>
                  <select value={dayOfMonth} onChange={e => setDayOfMonth(e.target.value)} style={selectStyle}>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* End condition */}
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#0c4a6e', marginBottom: 10 }}>End Condition</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {[['forever','♾ Run Forever'],['cycles','🔢 After N Cycles'],['date','📅 Until Date']].map(([val, lbl]) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${endType === val ? '#0369a1' : '#d1d5db'}`, background: endType === val ? '#e0f2fe' : '#fff', fontSize: 13, fontWeight: endType === val ? 700 : 500 }}>
                    <input type="radio" value={val} checked={endType === val} onChange={() => setEndType(val)} style={{ accentColor: '#0369a1' }} />
                    {lbl}
                  </label>
                ))}
              </div>
              {endType === 'cycles' && (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Number of cycles:</label>
                  <input type="number" min={1} value={maxCycles} onChange={e => setMaxCycles(e.target.value)} style={{ ...selectStyle, width: 80, textAlign: 'center' }} />
                </div>
              )}
              {endType === 'date' && (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>End date:</label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ ...selectStyle, padding: '5px 10px' }} />
                </div>
              )}
            </div>

            {/* Summary */}
            <p style={{ fontSize: 12, color: '#0369a1', marginTop: 12, marginBottom: 0 }}>
              {frequency === 'hourly' && 'Runs every hour'}
              {frequency === 'daily'  && `Runs daily at ${time} UTC`}
              {frequency === 'weekly' && `Runs every ${DOW_LABELS[dayOfWeek]} at ${time} UTC`}
              {frequency === 'monthly' && `Runs on day ${dayOfMonth} of each month at ${time} UTC`}
              {endType === 'cycles' && ` · stops after ${maxCycles} cycle${maxCycles !== 1 ? 's' : ''}`}
              {endType === 'date' && endDate && ` · ends ${new Date(endDate).toLocaleDateString()}`}
            </p>
          </div>
        )}
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14, marginBottom: 16 }}>{error}</div>}

      <button onClick={handleAnalyse} disabled={!canAnalyse || loading} style={{ ...primaryBtnStyle, opacity: !canAnalyse || loading ? 0.6 : 1, cursor: !canAnalyse || loading ? 'not-allowed' : 'pointer' }}>
        {loading ? 'Analysing…' : 'Analyse File →'}
      </button>
    </div>
  );
}

// ─── Step 2: Mapping ──────────────────────────────────────────────────────────
function MappingStep({ analysisResult, onConfirm, onBack }) {
  const { fields: initialFields, suggestedMatchKeys, availableCollections, collectionFields, rows, warnings: analysisWarnings = [] } = analysisResult;
  const [fields, setFields] = useState(() =>
    initialFields.map(f => ({ fileField: f.fileField, samples: f.samples, confidence: f.confidence, collection: f.suggestedCollection, firestoreField: f.suggestedField, enabled: true }))
  );
  const [matchKeys, setMatchKeys] = useState(() => ({ ...suggestedMatchKeys }));

  const updateField = (idx, key, value) => setFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: value } : f));
  const getFieldsForCollection = col => Object.keys(collectionFields[col] || {});
  const fileFieldsByCollection = useCallback(() => {
    const byCol = {};
    fields.filter(f => f.enabled).forEach(f => { if (!byCol[f.collection]) byCol[f.collection] = []; if (!byCol[f.collection].includes(f.fileField)) byCol[f.collection].push(f.fileField); });
    return byCol;
  }, [fields]);
  const activeCollections = [...new Set(fields.filter(f => f.enabled).map(f => f.collection))];
  const handleConfirm = () => {
    const mapping = fields.filter(f => f.enabled && f.collection && f.firestoreField).map(f => ({ fileField: f.fileField, collection: f.collection, firestoreField: f.firestoreField }));
    onConfirm({ mapping, matchKeys, rows });
  };

  // Detect collection mismatch: fields re-assigned to a collection with low schema coverage
  const collectionMismatches = (() => {
    const issues = [];
    const byCol = {};
    fields.filter(f => f.enabled).forEach(f => {
      if (!byCol[f.collection]) byCol[f.collection] = { total: 0, lowConf: 0 };
      byCol[f.collection].total++;
      if (f.confidence < 60) byCol[f.collection].lowConf++;
    });
    Object.entries(byCol).forEach(([col, { total, lowConf }]) => {
      if (total >= 3 && lowConf / total > 0.6) {
        issues.push(`Most fields have low match for "${col}" — verify selection.`);
      }
    });
    return issues;
  })();

  const allWarnings = [
    ...analysisWarnings.map(w => w.message),
    ...collectionMismatches,
  ];

  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 6, fontSize: 18 }}>Map File Fields to Firestore</h3>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 4 }}>{analysisResult.totalRows} rows detected. Review and adjust the suggested mappings below.</p>
      <p style={{ color: '#6b7280', fontSize: 13, marginBottom: allWarnings.length ? 12 : 20 }}>The <strong>Match Key</strong> per collection identifies existing records to update instead of creating duplicates.</p>

      {allWarnings.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 6 }}>⚠ Review before proceeding</div>
          {allWarnings.map((w, i) => (
            <div key={i} style={{ fontSize: 13, color: '#b45309', marginBottom: 3 }}>• {w}</div>
          ))}
        </div>
      )}

      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={thStyle}>On/Off</th><th style={thStyle}>File Field</th><th style={thStyle}>Sample Values</th><th style={thStyle}>→ Collection</th><th style={thStyle}>→ Firestore Field</th><th style={thStyle}>Match</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f, idx) => (
              <tr key={f.fileField} style={{ borderBottom: '1px solid #f3f4f6', opacity: f.enabled ? 1 : 0.45 }}>
                <td style={tdStyle}><input type="checkbox" checked={f.enabled} onChange={e => updateField(idx, 'enabled', e.target.checked)} /></td>
                <td style={{ ...tdStyle, fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{f.fileField}<ConfidenceBadge score={f.confidence} /></td>
                <td style={{ ...tdStyle, color: '#6b7280', fontSize: 12, maxWidth: 160 }}>{f.samples.slice(0, 3).join(', ')}</td>
                <td style={tdStyle}>
                  <select value={f.collection} disabled={!f.enabled} onChange={e => updateField(idx, 'collection', e.target.value)} style={selectStyle}>
                    {availableCollections.map(c => <option key={c} value={c}>{COLLECTION_LABELS[c] || c}</option>)}
                  </select>
                </td>
                <td style={tdStyle}>
                  <select value={f.firestoreField} disabled={!f.enabled} onChange={e => updateField(idx, 'firestoreField', e.target.value)} style={selectStyle}>
                    {getFieldsForCollection(f.collection).map(ff => <option key={ff} value={ff}>{ff}</option>)}
                    <option value={f.fileField}>{f.fileField} (keep as-is)</option>
                  </select>
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {matchKeys[f.collection] === f.fileField && <span style={{ color: '#2563eb', fontWeight: 700, fontSize: 12 }}>🔑</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {activeCollections.length > 0 && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#0c4a6e' }}>🔑 Match Keys — used to find and update existing records</div>
          {activeCollections.map(col => (
            <div key={col} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ minWidth: 110, fontWeight: 600, fontSize: 13 }}>{COLLECTION_LABELS[col] || col}:</span>
              <select value={matchKeys[col] || ''} onChange={e => setMatchKeys(prev => ({ ...prev, [col]: e.target.value || null }))} style={{ ...selectStyle, minWidth: 180 }}>
                <option value="">(none — always create new)</option>
                {(fileFieldsByCollection()[col] || []).map(ff => <option key={ff} value={ff}>{ff}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onBack} style={secondaryBtnStyle}>← Back</button>
        <button onClick={handleConfirm} style={primaryBtnStyle}>Preview Changes →</button>
      </div>
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────
function PreviewModal({ summary, matchKeys, onApprove, onReject, loading }) {
  // Warn if a match key was set but found 0 existing records (all rows → new)
  const duplicateRisks = Object.entries(summary)
    .filter(([col, stats]) => matchKeys?.[col] && stats.updatedRecords === 0 && stats.newRecords > 0)
    .map(([col]) => `Match key found 0 matches in ${COLLECTION_LABELS[col] || col} — duplicates may be created.`);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '32px 36px', maxWidth: 520, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        <h2 style={{ marginTop: 0, fontSize: 20 }}>⚠️ Confirm Data Ingest</h2>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>The following changes will be made to your Firestore database. Review carefully before approving.</p>

        {duplicateRisks.length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 4 }}>⚠ Possible duplicates</div>
            {duplicateRisks.map((msg, i) => <div key={i} style={{ fontSize: 13, color: '#b45309' }}>• {msg}</div>)}
          </div>
        )}

        {Object.entries(summary).map(([col, stats]) => (
          <div key={col} style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 16px', marginBottom: 10, border: '1px solid #e5e7eb' }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{COLLECTION_LABELS[col] || col}</div>
            <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
              {stats.newRecords > 0 && <span style={{ color: '#16a34a', fontWeight: 600 }}>+{stats.newRecords} new</span>}
              {stats.updatedRecords > 0 && <span style={{ color: '#2563eb', fontWeight: 600 }}>✎ {stats.updatedRecords} updated</span>}
              {stats.newRecords === 0 && stats.updatedRecords === 0 && <span style={{ color: '#6b7280' }}>No changes</span>}
            </div>
            {stats.note && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{stats.note}</div>}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button onClick={onReject} disabled={loading} style={secondaryBtnStyle}>Reject</button>
          <button onClick={onApprove} disabled={loading} style={{ ...primaryBtnStyle, opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Ingesting…' : 'Approve & Ingest'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Done ─────────────────────────────────────────────────────────────
function DoneStep({ results, scheduleConfig, saveResult, onReset }) {
  const totalCreated = Object.values(results).reduce((s, r) => s + (r.created || 0), 0);
  const totalUpdated = Object.values(results).reduce((s, r) => s + (r.updated || 0), 0);
  return (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
      <h3 style={{ fontSize: 20, marginBottom: 8 }}>Ingest Complete</h3>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 6 }}>
        <strong>{scheduleConfig?.name}</strong> — {totalCreated} created, {totalUpdated} updated
      </p>
      {scheduleConfig?.type === 'recurring' && saveResult?.nextRun && (
        <p style={{ color: '#0369a1', fontSize: 13, marginBottom: 16 }}>
          🔁 Next auto-pull scheduled for {new Date(saveResult.nextRun).toLocaleString()}
        </p>
      )}
      <div style={{ display: 'inline-block', textAlign: 'left', marginBottom: 28, background: '#f9fafb', borderRadius: 12, padding: '14px 20px' }}>
        {Object.entries(results).map(([col, stats]) => (
          <div key={col} style={{ marginBottom: 6, fontSize: 14 }}>
            <strong>{COLLECTION_LABELS[col] || col}:</strong>{' '}
            {stats.created > 0 && <span style={{ color: '#16a34a' }}>{stats.created} created</span>}
            {stats.created > 0 && stats.updated > 0 && <span>, </span>}
            {stats.updated > 0 && <span style={{ color: '#2563eb' }}>{stats.updated} updated</span>}
            {stats.errors > 0 && <span style={{ color: '#dc2626' }}>, {stats.errors} errors</span>}
          </div>
        ))}
      </div>
      <div><button onClick={onReset} style={primaryBtnStyle}>New Upload Request</button></div>
    </div>
  );
}

// ─── Requests List ────────────────────────────────────────────────────────────
function RequestsList({ schedules, onNew, loading }) {
  if (loading) return <div style={{ color: '#6b7280', fontSize: 14, padding: '20px 0' }}>Loading requests…</div>;

  const statusBadge = (s) => {
    const map = { active: ['#16a34a','#dcfce7','Active'], completed: ['#6b7280','#f3f4f6','Completed'], pending: ['#d97706','#fef3c7','Pending'], paused: ['#7c3aed','#ede9fe','Paused'] };
    const [color, bg, label] = map[s] || ['#6b7280','#f3f4f6', s];
    return <span style={{ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 20, padding: '2px 10px' }}>{label}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>{schedules.length} upload request{schedules.length !== 1 ? 's' : ''}</div>
        <button onClick={onNew} style={primaryBtnStyle}>+ New Request</button>
      </div>

      {schedules.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No upload requests yet</div>
          <div style={{ fontSize: 13 }}>Click <strong>New Request</strong> to create your first file integration.</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Frequency</th>
                <th style={thStyle}>Next Run</th>
                <th style={thStyle}>End Condition</th>
                <th style={thStyle}>Cycles</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Created</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{s.name}</td>
                  <td style={tdStyle}>{s.type === 'one-time' ? '⬆ One-time' : '🔁 Recurring'}</td>
                  <td style={tdStyle}>
                    {s.type === 'recurring' ? (
                      <span>
                        {s.frequency}
                        {s.frequency === 'weekly' && ` (${DOW_LABELS[s.dayOfWeek]})`}
                        {s.frequency === 'monthly' && ` (day ${s.dayOfMonth})`}
                        {s.frequency !== 'hourly' && ` @ ${s.time} UTC`}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={tdStyle}>
                    {s.nextRun ? new Date(s.nextRun).toLocaleString() : '—'}
                  </td>
                  <td style={tdStyle}>
                    {s.type !== 'recurring' ? '—' :
                      s.endType === 'forever' ? '♾ Forever' :
                      s.endType === 'cycles' ? `${s.completedCycles || 0} / ${s.maxCycles} cycles` :
                      s.endDate ? `Until ${new Date(s.endDate).toLocaleDateString()}` : '—'
                    }
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{s.type === 'recurring' ? (s.completedCycles || 0) : '—'}</td>
                  <td style={tdStyle}>{statusBadge(s.status || 'pending')}</td>
                  <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Rollback Confirm Modal ───────────────────────────────────────────────────
function RollbackModal({ record, salonId, onDone, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const snap = record.snapshotMeta || {};
  const totalCreated = snap.totalCreated ?? 0;
  const totalUpdated = snap.totalUpdated ?? 0;

  const handleRollback = async () => {
    setLoading(true); setError('');
    try {
      await rollbackIngest(salonId, record.snapshotId);
      onDone();
    } catch (e) {
      setError(e.message); setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '28px 32px', maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        <h3 style={{ marginTop: 0, fontSize: 18, color: '#dc2626' }}>↩ Undo Ingest Changes</h3>
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
          This will reverse all changes made by <strong>"{record.scheduleName}"</strong>:
        </p>
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
          {totalCreated > 0 && <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 4 }}>🗑 Delete {totalCreated} record{totalCreated !== 1 ? 's' : ''} that were created</div>}
          {totalUpdated > 0 && <div style={{ fontSize: 13, color: '#b45309' }}>↩ Restore {totalUpdated} record{totalUpdated !== 1 ? 's' : ''} to their previous state</div>}
          {totalCreated === 0 && totalUpdated === 0 && <div style={{ fontSize: 13, color: '#6b7280' }}>No tracked changes to reverse.</div>}
        </div>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>
          Once rolled back, you cannot re-apply these changes. A new import will be needed.
        </p>
        {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={loading} style={secondaryBtnStyle}>Cancel</button>
          <button
            onClick={handleRollback}
            disabled={loading || (totalCreated === 0 && totalUpdated === 0)}
            style={{ ...primaryBtnStyle, background: '#dc2626', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Rolling back…' : 'Confirm Rollback'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── History Table ────────────────────────────────────────────────────────────
function HistoryTable({ history, loading, salonId, onRefresh }) {
  const [confirmRecord, setConfirmRecord] = useState(null);

  if (loading) return <div style={{ color: '#6b7280', fontSize: 14, padding: '20px 0' }}>Loading history…</div>;
  if (!history.length) return (
    <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No upload history yet</div>
      <div style={{ fontSize: 13 }}>History will appear here after files are ingested.</div>
    </div>
  );

  const statusBadge = (status) => {
    const map = {
      success:     ['#16a34a','#dcfce7','Success'],
      partial:     ['#d97706','#fef3c7','Partial'],
      failure:     ['#dc2626','#fef2f2','Failed'],
      rolled_back: ['#6b7280','#f3f4f6','Rolled back'],
    };
    const [color, bg, label] = map[status] || ['#6b7280','#f3f4f6', status];
    return <span style={{ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 20, padding: '2px 10px' }}>{label}</span>;
  };

  return (
    <div>
      {confirmRecord && (
        <RollbackModal
          record={confirmRecord}
          salonId={salonId}
          onDone={() => { setConfirmRecord(null); onRefresh(); }}
          onCancel={() => setConfirmRecord(null)}
        />
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={thStyle}>Request Name</th>
              <th style={thStyle}>Records</th>
              <th style={thStyle}>Collections</th>
              <th style={thStyle}>Triggered By</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Date / Time</th>
              <th style={thStyle}>Undo</th>
            </tr>
          </thead>
          <tbody>
            {history.map(h => {
              const totalCreated = h.results ? Object.values(h.results).reduce((s,r)=>s+(r.created||0),0) : 0;
              const totalUpdated = h.results ? Object.values(h.results).reduce((s,r)=>s+(r.updated||0),0) : 0;
              const canUndo = h.snapshotId && h.status !== 'rolled_back' && h.status !== 'failure';
              return (
                <tr key={h.id} style={{ borderBottom: '1px solid #f3f4f6', opacity: h.status === 'rolled_back' ? 0.6 : 1 }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{h.scheduleName || '—'}</td>
                  <td style={tdStyle}>
                    {h.results ? (
                      <span>
                        {totalCreated > 0 && <span style={{ color: '#16a34a', fontWeight: 600, marginRight: 6 }}>+{totalCreated}</span>}
                        {totalUpdated > 0 && <span style={{ color: '#2563eb', fontWeight: 600 }}>✎{totalUpdated}</span>}
                        {totalCreated === 0 && totalUpdated === 0 && <span style={{ color: '#6b7280' }}>0</span>}
                        {h.results && Object.values(h.results).reduce((s,r)=>s+(r.errors||0),0) > 0 && (
                          <span style={{ color: '#dc2626', fontWeight: 600, marginLeft: 6 }}>⚠{Object.values(h.results).reduce((s,r)=>s+(r.errors||0),0)} err</span>
                        )}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: '#374151' }}>
                    {(h.collections || []).map(c => COLLECTION_LABELS[c] || c).join(', ') || '—'}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: h.triggeredBy === 'cron' ? '#7c3aed' : '#0369a1' }}>
                      {h.triggeredBy === 'cron' ? '🔁 Auto' : '⬆ Manual'}
                    </span>
                  </td>
                  <td style={tdStyle}>{statusBadge(h.status)}</td>
                  <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>{h.runAt ? new Date(h.runAt).toLocaleString() : '—'}</td>
                  <td style={tdStyle}>
                    {canUndo ? (
                      <button
                        onClick={() => setConfirmRecord({ ...h, snapshotMeta: { totalCreated, totalUpdated } })}
                        style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                      >
                        ↩ Undo
                      </button>
                    ) : (
                      <span style={{ color: '#d1d5db', fontSize: 12 }}>
                        {h.status === 'rolled_back' ? 'Rolled back' : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function FileIntegrationTab({ salonId }) {
  const [mainTab, setMainTab]     = useState('requests'); // 'requests' | 'history'
  const [wizardOpen, setWizardOpen] = useState(false);

  // Wizard state
  const [step, setStep]               = useState(STEPS.CONFIGURE);
  const [analysisResult, setAnalysis] = useState(null);
  const [mappingData, setMappingData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [ingestResults, setResults]   = useState(null);
  const [saveResult, setSaveResult]   = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [ingestLoading, setIngestLoading]   = useState(false);
  const [wizardError, setWizardError] = useState('');
  const [showModal, setShowModal]     = useState(false);

  // List data
  const [schedules, setSchedules]     = useState([]);
  const [history, setHistory]         = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [historyLoading, setHistoryLoading]     = useState(false);

  const loadSchedules = useCallback(() => {
    setSchedulesLoading(true);
    listSchedules(salonId).then(r => setSchedules(r.schedules || [])).catch(() => {}).finally(() => setSchedulesLoading(false));
  }, [salonId]);

  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    getHistory(salonId).then(r => setHistory(r.history || [])).catch(() => {}).finally(() => setHistoryLoading(false));
  }, [salonId]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);
  useEffect(() => { if (mainTab === 'history') loadHistory(); }, [mainTab, loadHistory]);

  const openWizard = () => {
    setStep(STEPS.CONFIGURE); setAnalysis(null); setMappingData(null);
    setPreviewData(null); setResults(null); setSaveResult(null);
    setWizardError(''); setShowModal(false); setWizardOpen(true);
  };

  const closeWizard = () => { setWizardOpen(false); loadSchedules(); };

  const handleAnalysed = (result) => { setAnalysis(result); setStep(STEPS.MAPPING); };

  const handleMappingConfirm = async (data) => {
    setMappingData(data); setPreviewLoading(true); setWizardError('');
    try {
      const result = await previewIngest(salonId, data.rows, data.mapping, data.matchKeys);
      setPreviewData(result); setShowModal(true); setStep(STEPS.PREVIEW);
    } catch (e) { setWizardError(e.message); }
    finally { setPreviewLoading(false); }
  };

  const handleApprove = async () => {
    setIngestLoading(true); setWizardError('');
    try {
      const sc = analysisResult.scheduleConfig;
      const result = await executeIngest(salonId, mappingData.rows, mappingData.mapping, mappingData.matchKeys, sc?.name);
      setResults(result.results);
      setShowModal(false);

      // Save the schedule/request record
      let saved = null;
      try {
        saved = await saveSchedule(salonId, {
          ...sc,
          fileUrl: analysisResult.fileUrl || null,
          mapping: mappingData.mapping,
          matchKeys: mappingData.matchKeys,
        });
        setSaveResult(saved);
      } catch (saveErr) {
        console.warn('Could not save schedule:', saveErr.message);
      }

      setStep(STEPS.DONE);
    } catch (e) { setWizardError(e.message); }
    finally { setIngestLoading(false); }
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px' }}>
      <div className="editor-page-header" style={{ marginBottom: 8 }}>
        <h2>File Integration</h2>
      </div>
      <p style={{ color: '#6b7280', fontSize: 14, marginTop: 0, marginBottom: 24 }}>
        Upload CSV or JSON files — one-time or on a recurring schedule — to create or update records directly in the database.
      </p>

      {/* Main tabs */}
      {!wizardOpen && (
        <div style={{ borderBottom: '1px solid #e5e7eb', display: 'flex', marginBottom: 24 }}>
          <TabBtn id="requests" active={mainTab === 'requests'} onClick={setMainTab}>📋 Upload Requests</TabBtn>
          <TabBtn id="history" active={mainTab === 'history'} onClick={setMainTab}>📜 Upload History</TabBtn>
        </div>
      )}

      {/* Wizard */}
      {wizardOpen && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <button onClick={closeWizard} style={{ ...secondaryBtnStyle, padding: '7px 14px', fontSize: 13 }}>← Back to Requests</button>
            <span style={{ color: '#6b7280', fontSize: 13 }}>New Upload Request</span>
          </div>
          <StepBar step={step} />
          {wizardError && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14, marginBottom: 20 }}>{wizardError}</div>}

          {step === STEPS.CONFIGURE && <ConfigureStep salonId={salonId} onAnalysed={handleAnalysed} />}
          {step === STEPS.MAPPING && analysisResult && <MappingStep analysisResult={analysisResult} onConfirm={handleMappingConfirm} onBack={() => setStep(STEPS.CONFIGURE)} />}
          {step === STEPS.PREVIEW && previewLoading && <div style={{ color: '#6b7280', fontSize: 14 }}>Checking existing records…</div>}
          {step === STEPS.DONE && ingestResults && <DoneStep results={ingestResults} scheduleConfig={analysisResult?.scheduleConfig} saveResult={saveResult} onReset={closeWizard} />}

          {showModal && previewData && (
            <PreviewModal summary={previewData.summary} matchKeys={mappingData?.matchKeys} onApprove={handleApprove} onReject={() => { setShowModal(false); setStep(STEPS.MAPPING); }} loading={ingestLoading} />
          )}
        </div>
      )}

      {/* Requests tab */}
      {!wizardOpen && mainTab === 'requests' && (
        <RequestsList schedules={schedules} onNew={openWizard} loading={schedulesLoading} />
      )}

      {/* History tab */}
      {!wizardOpen && mainTab === 'history' && (
        <HistoryTable history={history} loading={historyLoading} salonId={salonId} onRefresh={loadHistory} />
      )}
    </div>
  );
}
